import { createFileRoute, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { projectBySlugQueryOptions } from "@/lib/portfolio";

export const Route = createFileRoute("/work/$slug")({
  loader: async ({ context, params }) => {
    const data = await context.queryClient.ensureQueryData(projectBySlugQueryOptions(params.slug));
    if (!data) throw notFound();
    return data;
  },
  head: ({ params, loaderData }) => {
    const project = loaderData;
    const title = project ? `${project.title} — Mind Media` : "Project — Mind Media";
    const description = project?.summary
      ? project.summary.replace(/\s+/g, " ").trim().slice(0, 155)
      : "Explore work by Mind Media — strategy-led design, development, and marketing built to convert.";
    const url = `https://mindmedia.co/work/${params.slug}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: WorkDetail,
  notFoundComponent: () => (
    <main className="mx-auto max-w-3xl px-4 py-24 text-center">
      <p className="text-muted-foreground">Project not found.</p>
    </main>
  ),
  errorComponent: ({ error }) => (
    <main className="mx-auto max-w-3xl px-4 py-24 text-center">
      <p className="text-destructive">{error.message}</p>
    </main>
  ),
});

function WorkDetail() {
  const { slug } = Route.useParams();
  const { data: project } = useSuspenseQuery(projectBySlugQueryOptions(slug));
  if (!project) return null;
  return (
    <main className="mx-auto max-w-3xl px-4 py-16">
      <h1 className="font-display text-3xl font-bold text-gray-950 sm:text-4xl">{project.title}</h1>
      <p className="mt-2 text-sm text-muted-foreground">{project.categoryLabel}</p>
      {project.cover && (
        <img src={project.cover} alt={project.title} className="mt-6 w-full rounded-2xl" />
      )}
      {project.summary && <p className="mt-6 text-base leading-relaxed text-gray-800">{project.summary}</p>}
    </main>
  );
}
